<template>
    <div
        v-if="categories.length > 0 && categories[0] && categories[0].ueatId"
        class="l-grid"
    >
        <div class="c-thumb-list" :class="{ 'c-thumb-list--slider': slider }">
            <h2 class="c-thumb-list__title">
                <slot name="title">
                    <span :class="titleClass">{{ title }}</span>
                </slot>
            </h2>

            <div>
                <span
                    class="o-sentinel o-sentinel--top"
                    ref="sentinelTop"
                ></span>
                <span
                    class="o-sentinel o-sentinel--bottom"
                    ref="sentinelBottom"
                ></span>
                <nav class="c-thumb-list__nav" ref="nav">
                    <flickity ref="navSlider" :options="navSliderOpts">
                        <Btn
                            v-for="(category, i) in categories"
                            v-if="category !== null"
                            :key="`btn-${category.ueatId}-${i}`"
                            class="c-btn--rounded"
                            :class="{ 'is-active': category.ueatId === filter }"
                            @click.native="
                                slider
                                    ? filterThumbs(category.ueatId)
                                    : scrollTo(category.ueatId)
                            "
                        >
                            <slot>
                                {{ removeEmojis(category.title) }}
                                <span>{{ category.count }}</span>
                            </slot>
                        </Btn>
                    </flickity>
                </nav>
                <div v-if="!slider">
                    <div
                        v-for="(category, i) in categories"
                        v-if="category !== null"
                        :key="`category-${category.ueatId}-${i}`"
                        :id="`category-${category.ueatId}`"
                        class="c-thumb-list__grid"
                    >
                        <h2 class="c-thumb-list__sub | t-t2-serif">
                            {{ category.title }}
                        </h2>
                        <Thumb
                            v-for="(thumb, i) in getCategoryItems(
                                category.ueatId
                            )"
                            :key="i"
                            :thumbs="thumb"
                        />
                    </div>
                </div>
                <div v-else ref="sliderWrapper">
                    <flickity
                        v-if="thumbs"
                        :key="filter"
                        ref="thumbSlider"
                        class="c-thumb-list__grid"
                        :options="thumbSliderOpts"
                    >
                        <div
                            v-for="(thumb, i) in thumbs.filter(
                                (thumb) => thumb.categoryId === filter
                            )"
                            :key="`slide-${i}`"
                            class="c-thumb-list__slide"
                        >
                            <Thumb
                                :thumbs="thumb"
                                @imageLoaded="$refs.thumbSlider.resize()"
                            />
                        </div>
                    </flickity>
                    <div class="flickity-nav">
                        <button
                            class="flickity-arrow flickity-arrow--prev"
                            aria-label="Précédent"
                            @click="$refs.thumbSlider.previous()"
                        >
                            <Icon icon="arrow-left" />
                        </button>
                        <button
                            class="flickity-arrow flickity-arrow--next"
                            aria-label="Suivant"
                            @click="$refs.thumbSlider.next()"
                        >
                            <Icon icon="arrow-right" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/* eslint-disable */

import Btn from "components/Btn";
import Icon from "components/Icon";
import Thumb from "components/Thumb";

import Flickity from "vue-flickity";
import { mapGetters } from "vuex";
import flattenDepth from "lodash/flattenDepth";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";

import { removeEmojis } from "utils/utils";

export default {
    name: "ThumbList",
    components: {
        Btn,
        Icon,
        Thumb,
        Flickity,
    },
    props: {
        title: {
            type: String,
            default: "Découvrez le menu",
        },
        titleClass: {
            type: String,
            default: "t-t1",
        },
        categories: {
            type: Array,
            default: () => [],
        },
        limit: {
            type: Number,
            default: null,
        },
        slider: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        observer: false,
        filter: null,
        onResize: null,
        scrollOffset: 0,
        removeEmojis,
        navSliderOpts: {
            contain: true,
            pageDots: false,
            cellAlign: "center",
            prevNextButtons: false,
            freeScroll: true,
        },
        thumbSliderOpts: {
            contain: true,
            pageDots: false,
            cellAlign: "left",
            prevNextButtons: false,
            watchCSS: true,
        },
    }),
    methods: {
        filterThumbs(category) {
            TweenLite.to(this.$refs.sliderWrapper, 0.2, {
                opacity: 0,
                ease: Power2.easeIn,
                onStart: () => {
                    TweenLite.set(this.$refs.sliderWrapper, {
                        height: this.$refs.sliderWrapper.offsetHeight,
                    });
                },
                onComplete: () => {
                    // Then filters the thumbs
                    this.filter = category;

                    TweenLite.to(this.$refs.sliderWrapper, 0.3, {
                        opacity: 1,
                        ease: Power2.easeOut,
                        onComplete: () => {
                            TweenLite.set(this.$refs.sliderWrapper, {
                                clearProps: "all",
                            });
                        },
                    });
                },
            });
        },
        scrollTo(category) {
            const $target = document.getElementById(`category-${category}`);

            // Then filters the thumbs
            this.filter = category;

            TweenLite.to(window, 1, {
                scrollTo: {
                    y:
                        $target.getBoundingClientRect().top +
                        window.scrollY -
                        this.scrollOffset,
                    autoKill: false,
                },
                ease: Expo.easeInOut,
            });
        },
        setOffsetScroll() {
            if (!this.$refs.nav) return;

            this.scrollOffset = this.$refs.nav.offsetHeight;
            this.scrollOffset +=
                window.innerWidth < 992
                    ? document.querySelector(".l-header").offsetHeight
                    : 0;
        },
    },
    computed: {
        ...mapGetters("ueat", ["getCategoryItems"]),

        thumbs() {
            if (!this.categories) return;

            if (isEmpty(this.categories)) return;

            const _ = this;
            const _thumbs = [];
            const _categories = _.categories;

            _categories.forEach(function (_category, index) {
                // Double check for a valid category object
                if (typeof _category === "undefined" || !_category) return;

                // Get items by category
                let _items = _.getCategoryItems(_category.ueatId);

                // Slice when needed
                _items = _.limit ? _items.slice(0, _.limit) : _items;

                // Add `push` slide
                _items.push({
                    title: _category.title,
                    description: _category.description,
                    categoryId: _category.ueatId,
                    push: true,
                });

                // Push
                _thumbs.push(_items);
            });

            return flattenDepth(_thumbs, 2);
        },
    },
    mounted() {
        // Default tab
        if (this.categories && this.categories.length > 0 && this.slider)
            this.filter = this.categories[0].ueatId;

        // Add class when menu nav is sticky
        if (!this.slider && this.$refs.nav) {
            this.observer = new IntersectionObserver(
                (observables) => {
                    for (const observable of observables) {
                        const targetInfo = observable.boundingClientRect;
                        const rootBoundsInfo = observable.rootBounds;
                        const ratio = observable.intersectionRatio;

                        if (
                            observable.target.classList.contains(
                                "o-sentinel--top"
                            )
                        ) {
                            if (targetInfo.bottom < rootBoundsInfo.top) {
                                this.$refs.nav.classList.add("is-sticky");
                            } else if (
                                targetInfo.bottom >= rootBoundsInfo.top &&
                                targetInfo.bottom < rootBoundsInfo.bottom
                            ) {
                                this.$refs.nav.classList.remove("is-sticky");
                            }
                        } else if (
                            observable.target.classList.contains(
                                "o-sentinel--bottom"
                            )
                        ) {
                            if (
                                targetInfo.bottom > rootBoundsInfo.top &&
                                ratio === 1
                            ) {
                                this.$refs.nav.classList.add("is-sticky");
                            } else if (
                                targetInfo.top < rootBoundsInfo.top &&
                                targetInfo.bottom < rootBoundsInfo.bottom
                            ) {
                                this.$refs.nav.classList.remove("is-sticky");
                            }
                        }
                    }
                },
                {
                    threshold: [0, 1],
                }
            );

            this.observer.observe(this.$refs.sentinelTop);
            this.observer.observe(this.$refs.sentinelBottom);
        }

        // Set sizes
        this.setOffsetScroll();

        // All Window events
        window.addEventListener(
            "resize",
            (this.onResize = debounce(this.setOffsetScroll, 100))
        );
    },
    beforeDestroy() {
        if (this.observer) {
            this.observer.unobserve(this.$refs.sentinelTop);
            this.observer.unobserve(this.$refs.sentinelBottom);
        }

        // Remove Window events
        window.removeEventListener("resize", this.onResize);
    },
};
</script>

<style lang="scss">
.o-sentinel {
    position: absolute;
    right: 0;
    left: 0;
    visibility: hidden;

    &--top {
        top: calc(-#{$topbar-height} - 1px);
        height: 1px;
    }

    &--bottom {
        height: 1px;
        bottom: -1px;
    }

    @screen md {
        &--top {
            top: -1px;
        }
    }
}
</style>
